import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { organizationService } from '../../services/organizationService';
import { Organization } from '../../types/organization';
import { useOrganizations } from '../../hooks/useOrganizations';
import toast from 'react-hot-toast';
import { BuildingOfficeIcon, MapPinIcon, UserCircleIcon, CloudArrowUpIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useStorage } from '../../hooks/useStorage';

import { useLanguage } from '../../contexts/LanguageContext';
import { formatTaxNumber, formatRegistrationNumber } from '../../utils/validation';

interface FormData {
  name: string;
  taxNumber: string;
  registrationNumber: string;
  address: {
    street: string;
    city: string;
    postalCode: string;
    country: string;
  };
  contact: {
    name: string;
    email: string;
    phone: string;
    position: string;
  };
}

const initialFormData: FormData = {
  name: '',
  taxNumber: '',
  registrationNumber: '',
  address: {
    street: '',
    city: '',
    postalCode: '',
    country: ''
  },
  contact: {
    name: '',
    email: '',
    phone: '',
    position: ''
  }
};

const FORM_SECTIONS = {
  company: {
    title: 'Company Information',
    icon: BuildingOfficeIcon,
    color: 'from-blue-500 to-indigo-600',
    fields: [
      { name: 'name', label: 'Company Name', required: true },
      { name: 'taxNumber', label: 'Tax Number', required: true },
      { name: 'registrationNumber', label: 'Registration Number', required: true }
    ]
  },
  address: {
    title: 'Registered Address',
    icon: MapPinIcon,
    color: 'from-emerald-500 to-teal-600',
    fields: [
      { name: 'address.street', label: 'Street', required: true },
      { name: 'address.city', label: 'City', required: true },
      { name: 'address.postalCode', label: 'Postal Code', required: true },
      { name: 'address.country', label: 'Country', required: true }
    ]
  },
  contact: {
    title: 'Contact Person',
    icon: UserCircleIcon,
    color: 'from-purple-500 to-pink-600',
    fields: [
      { name: 'contact.name', label: 'Name', required: true },
      { name: 'contact.email', label: 'Email', required: true },
      { name: 'contact.phone', label: 'Phone', required: true },
      { name: 'contact.position', label: 'Position', required: false }
    ]
  }
};

interface OrganizationSettingsProps {
  organization: Organization;
  isEditing: boolean;
  onEditingChange: (editing: boolean) => void;
  onOrganizationUpdate: (org: Organization) => void;
  hideButtons?: boolean; // Modal-ban elrejtjük a gombokat
}

export const OrganizationSettings: React.FC<OrganizationSettingsProps> = ({
  organization,
  isEditing,
  onEditingChange,
  onOrganizationUpdate,
  hideButtons = false
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const { uploadFile, deleteFile } = useStorage();
  const [isUploading, setIsUploading] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    setFormData({
      name: organization.name,
      taxNumber: organization.taxNumber,
      registrationNumber: organization.registrationNumber,
      address: organization.address,
      contact: organization.contact
    });
  }, [organization]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Apply formatting for tax number and registration number
    if (name === 'taxNumber') {
      const formattedValue = formatTaxNumber(value);
      setFormData(prev => ({ ...prev, [name]: formattedValue }));
      return;
    } else if (name === 'registrationNumber') {
      const formattedValue = formatRegistrationNumber(value);
      setFormData(prev => ({ ...prev, [name]: formattedValue }));
      return;
    }
    
    // Handle other fields normally
    if (name.includes('.')) {
      const [section, field] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...(prev[section as keyof FormData] as Record<string, string>),
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await organizationService.updateOrganization(organization.id, formData);

      const updatedOrgData: Organization = {
        ...organization,
        ...formData,
        updatedAt: new Date()
      };

      onOrganizationUpdate(updatedOrgData);

      toast.success(typeof t('common.messages.organization.updateSuccess') === 'string' 
        ? t('common.messages.organization.updateSuccess') 
        : 'Szervezet sikeresen frissítve');
      onEditingChange(false);
    } catch (error) {
      console.error('Error updating organization:', error);
      toast.error(typeof t('common.messages.organization.updateError') === 'string'
        ? t('common.messages.organization.updateError')
        : 'Hiba történt a szervezet frissítésekor');
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      const path = `organizations/${organization.id}/logo`;
      const logoUrl = await uploadFile(file, path);

      const updatedOrg = {
        ...organization,
        logoUrl
      } as Organization;

      await organizationService.updateOrganization(organization.id, updatedOrg);
      onOrganizationUpdate(updatedOrg);

      toast.success(typeof t('common.messages.organization.logoUploadSuccess') === 'string'
        ? t('common.messages.organization.logoUploadSuccess')
        : 'Logó sikeresen feltöltve');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error(typeof t('common.messages.organization.logoUploadError') === 'string'
        ? t('common.messages.organization.logoUploadError')
        : 'Hiba történt a logó feltöltésekor');
    } finally {
      setIsUploading(false);
    }
  };

  const handleLogoDelete = async () => {
    if (!organization?.logoUrl) return;

    try {
      const path = `organizations/${organization.id}/logo`;
      await deleteFile(path);

      const updatedOrg = {
        ...organization,
        logoUrl: null
      } as Organization;

      await organizationService.updateOrganization(organization.id, updatedOrg);
      onOrganizationUpdate(updatedOrg);

      toast.success('Logo removed successfully');
    } catch (error) {
      console.error('Error removing logo:', error);
      toast.error('Failed to remove logo');
    }
  };

  const renderField = (label: string, name: string, required: boolean = true) => {
    const getValue = (path: string) => {
      const keys = path.split('.');
      let value: any = formData;
      for (const key of keys) {
        if (value === undefined) return '';
        value = value[key];
      }
      return value || '';
    };

    // Add placeholders for tax number and registration number fields
    let placeholder = '';
    if (name === 'taxNumber') {
      placeholder = '12345678-1-23';
    } else if (name === 'registrationNumber') {
      placeholder = '01-10-123456';
    }

    return (
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {typeof label === 'string' ? label : name} {required && <span className="text-red-500">*</span>}
        </label>
        {isEditing ? (
          <input
            type="text"
            name={name}
            value={getValue(name)}
            onChange={handleChange}
            required={required}
            placeholder={placeholder}
            className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg py-2 px-2 sm:py-2.5 sm:px-4 text-gray-700 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 text-sm sm:text-base overflow-hidden text-ellipsis shadow-sm"
          />
        ) : (
          <p className="py-2.5 px-4 bg-gray-100 dark:bg-gray-700 rounded-lg text-gray-700 dark:text-gray-300 overflow-hidden text-ellipsis">{getValue(name) || '—'}</p>
        )}
      </div>
    );
  };

  const getTranslation = (key: string, fallback: string): string => {
    const translation = t(key);
    return typeof translation === 'string' ? translation : fallback;
  };

  return (
    <div className="space-y-8 mb-4">
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 sm:gap-0">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
          {getTranslation('organizations.fields.organizationDetails', 'Szervezeti adatok')}
        </h2>
        {!isEditing && (user?.role === 'admin' || user?.role === 'super_admin') && (
          <button
            onClick={() => onEditingChange(true)}
            className="bg-gradient-to-r from-primary-500 to-primary-600 text-white px-6 py-2.5 rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all duration-200 font-medium shadow-lg shadow-primary-500/25"
          >
            {getTranslation('organizations.buttons.editDetails', 'Adatok szerkesztése')}
          </button>
        )}
      </div>

      <form id="org-settings-form" onSubmit={handleSubmit} className="space-y-8">
        {Object.entries(FORM_SECTIONS).map(([sectionKey, section]) => (
          <div
            key={sectionKey}
            className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-6 border border-gray-200 dark:border-gray-700 shadow-md"
          >
            <div className="flex items-center space-x-3 mb-4 sm:mb-6">
              <div className={`p-2 sm:p-2.5 rounded-lg bg-gradient-to-br ${section.color}`}>
                <section.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 dark:text-gray-100">
                {getTranslation(`organizations.fields.${sectionKey}.title`, section.title)}
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {section.fields.map((field) => (
                <React.Fragment key={field.name}>
                  {renderField(
                    getTranslation(`organizations.fields.${sectionKey}.${field.name}`, field.label),
                    field.name,
                    field.required
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        ))}

        {isEditing && !hideButtons && (
          <div className="flex flex-col sm:flex-row sm:justify-end space-y-3 sm:space-y-0 sm:space-x-4">
            <button
              type="button"
              onClick={() => {
                onEditingChange(false);
              }}
              className="px-6 py-2.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 hover:border-gray-400 dark:hover:border-gray-500 transition-all duration-200 font-medium"
            >
              {getTranslation('organizations.fields.cancelChanges', 'Változtatások elvetése')}
            </button>
            <button
              type="submit"
              className="bg-gradient-to-r from-primary-500 to-primary-600 text-white px-6 py-2.5 rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all duration-200 font-medium shadow-lg shadow-primary-500/25"
            >
              {getTranslation('organizations.fields.saveChanges', 'Változtatások mentése')}
            </button>
          </div>
        )}
      </form>

      <div>
        <h3 className="text-lg font-medium text-gray-800 dark:text-gray-100 mb-4">
          {getTranslation('organizations.fields.logo.organizationLogo', 'Szervezet logója')}
        </h3>
        <div className="flex flex-col sm:flex-row items-start gap-4">
          <div className="w-32 h-32 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 overflow-hidden">
            {organization?.logoUrl ? (
              <div className="relative group">
                <img
                  src={organization.logoUrl}
                  alt="Organization logo"
                  className="w-full h-full object-contain"
                />
                <button
                  onClick={handleLogoDelete}
                  className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 
                           flex items-center justify-center text-white transition-opacity"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-500 dark:text-gray-400">
                {getTranslation('organizations.fields.logo.noLogo', 'Nincs logó')}
              </div>
            )}
          </div>

          <div className="flex-1 mt-2 sm:mt-0">
            <label className="block">
              <span className="sr-only">
                {getTranslation('organizations.fields.logo.chooseLogoFile', 'Logófájl kiválasztása')}
              </span>
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                disabled={isUploading}
                className="block w-full text-sm text-gray-500
                         file:mr-4 file:py-2 file:px-4
                         file:rounded-md file:border-0
                         file:text-sm file:font-medium
                         file:bg-primary-600 file:text-white
                         file:cursor-pointer file:hover:bg-primary-700
                         disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </label>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {getTranslation('organizations.fields.logo.recommendedSize', 'Ajánlott méret: 200x200 pixel')}
            </p>
          </div>
        </div>
      </div>


    </div>
  );
};

export default OrganizationSettings;
